import { useEffect, useRef, useState } from "react";
import { COMPARISON_STATE_PARAM, STATE_PARAM } from "../dropdowns/urlParams";
import {
  calculateGraphData,
  calculateYearlyTotals,
  filterData,
  getTotalTurnout,
  handleNoPartyReg,
  mergeYearlyTotals,
} from "../helpers/dataCalculation";
import { fetchDemoData } from "../helpers/services";
import { CALC_TYPE } from "../helpers/config";

export default function useDemoData({
  config,
  geo_type,
  geo,
  demo_filters,
  state,
  comparison_state,
  count_prefix,
  voteCountYears,
  view_type,
  calc_type = CALC_TYPE.VOTE_SHARE,
  comparison_years,
  vote_mode = false,
}) {
  let activeDemoRequest = useRef([]);

  let [demoLoadPercent, setDemoLoadPercent] = useState(true);
  let [filteredData, setFilteredData] = useState(null);
  let [totalTurnout, setTotalTurnout] = useState(null);
  let [calculatedData, setCalculatedData] = useState([]);
  let [demoErrorMessage, setDemoErrorMessage] = useState(false);

  let [lastModified, setLastModified] = useState("");
  let [showNoRegPartyWarning, setShowNoRegPartyWarning] = useState(false);
  let [demoHeader, setDemoHeader] = useState("");

  // only used by the data fetch dep array
  let count_prefix_dep = vote_mode ? count_prefix : null;

  const checkHistoricDataPresent = () => {
    let state_data = config.editorialNotes?.find((d) => {
      return d.State === state;
    });
    // Only show 'No Early Vote Data' error message when we know there is NoHistoricData from the editorial_notes.csv file
    // AND 'Final election' radio button is not selected
    if (
      state_data?.NoHistoricData === "Y" &&
      !voteCountYears.voteCountYear0.includes("final_eday")
    ) {
      setDemoErrorMessage(
        `There is no early vote data available for ${state}.`
      );
    }
  };

  const RACE_VALUE_REMAP = {
    "African-American": "Black",
    Asian: "Asian or Pacific Islander",
    Caucasian: "White",
    Hispanic: "Hispanic or Latino",
    "Native American": "Native American",
    "Other/Uncoded": "Other/Uncoded",
    Multiracial: "Other/Uncoded",
    B: "Black",
    A: "Asian or Pacific Islander",
    H: "Hispanic or Latino",
    W: "White",
    O: "Other/Uncoded",
    U: "Other/Uncoded",
    M: "Other/Uncoded",
    N: "Native American",
  };

  const VOTE_MODE_VALUE_REMAP = {
    A: "Absentee",
    E: "In-Person Early",
    P: "In-Person Election day",
    R: "Registered Didn't vote",
    U: "Unregistered",
    M: "Absentee",
    Y: "Unknown Voting Method",
  };

  const remapFields = () => {
    // Re-code Race and Vote Mode fields to readable values
    // See https://targetsmart.atlassian.net/browse/SE-317 re: race remapping

    if (config.VIEW_DATA) {
      config.VIEW_DATA = config.VIEW_DATA.map((item) => {
        let remappedItem = { ...item };

        // Remap race field
        if (RACE_VALUE_REMAP.hasOwnProperty(item.race)) {
          remappedItem.race = RACE_VALUE_REMAP[item.race];
        }

        // Remap voteModePriorElection field
        const VOTE_MODE_FIELD = "vote_mode_prior_election";
        if (VOTE_MODE_VALUE_REMAP.hasOwnProperty(item[VOTE_MODE_FIELD])) {
          remappedItem[VOTE_MODE_FIELD] =
            VOTE_MODE_VALUE_REMAP[item[VOTE_MODE_FIELD]];
        }

        return remappedItem;
      });
    }
  };

  const calculateData = () => {
    remapFields();

    let countFields = vote_mode
      ? config.VOTE_MODE_COUNT_FIELDS
      : config.COUNT_FIELDS;

    let fd = filterData(
      config.VIEW_DATA,
      geo,
      geo_type,
      demo_filters,
      countFields,
      config.DEMOGRAPHIC_MAPPING
    );
    setFilteredData(fd);
    let lastSelectedDemo = demo_filters[demo_filters.length - 1].key;
    handleNoPartyReg(
      lastSelectedDemo,
      fd,
      setShowNoRegPartyWarning,
      showNoRegPartyWarning
    );

    // Calculate the totals for each year for each category (cy, cyminus1, cyminus2)
    // and add those totals to the filteredData object
    // Necessary for VOTE MODE feature
    if (vote_mode) {
      const yearlyTotals = calculateYearlyTotals(fd);
      fd = mergeYearlyTotals(fd, yearlyTotals);
      setFilteredData(fd);
    }

    let tt = getTotalTurnout(fd);
    setTotalTurnout(tt);

    let selectedYearKeyValues = comparison_years?.map((y) => {
      let y_index = Object.values(config.YEAR_MAPPING).findIndex(
        (v) => v === y
      );
      return Object.keys(config.YEAR_MAPPING)[y_index];
    });

    let years = !!selectedYearKeyValues ? selectedYearKeyValues : config.YEARS;
    setCalculatedData(
      calculateGraphData(
        fd,
        tt,
        years,
        voteCountYears,
        demo_filters,
        calc_type,
        vote_mode,
        count_prefix
      )
    );

    let demoLabel = config.DEMO_OPTIONS.find((d) => {
      return d.value === lastSelectedDemo;
    })?.label;
    setDemoHeader(demoLabel);
    setDemoLoadPercent(true);
    setDemoErrorMessage(false);
    if (view_type === STATE_PARAM) {
      checkHistoricDataPresent(state);
    }
  };

  const getDemoData = (file_name) => {
    return new Promise((resolve, reject) => {
      if (!activeDemoRequest.current) activeDemoRequest.current = [];
      let demoRequest = fetchDemoData(
        file_name,
        geo_type,
        config.DATA_FOLDER,
        (_loadPercent) => {
          setDemoLoadPercent(
            activeDemoRequest.current
              .map((req) => req.loadPercent)
              .reduce((a, b) => a + b) / activeDemoRequest.current.length
          );
        },
        vote_mode,
        count_prefix
      );
      activeDemoRequest.current.push(demoRequest);
      demoRequest.promise
        .then(({ data, modTime }) => {
          setLastModified(modTime);
          resolve(data);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  useEffect(() => {
    if (!!config.VIEW_DATA) {
      calculateData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, demo_filters, count_prefix, calc_type, comparison_years, vote_mode]);

  useEffect(() => {
    setDemoLoadPercent(0);
    if (activeDemoRequest.current.length > 0) {
      for (let i = 0; i < activeDemoRequest.current.length; i++) {
        activeDemoRequest.current[i].abort();
      }
      activeDemoRequest.current = [];
    }

    switch (view_type) {
      case STATE_PARAM:
        getDemoData(state)
          .then((data) => {
            config.VIEW_DATA = data;
            setDemoLoadPercent(1);
            calculateData();
            activeDemoRequest.current = [];
          })
          .catch((e) => {
            if (e !== "user cancelled" && !e.name === "AbortError") {
              setDemoErrorMessage("An error occurred while fetching data");
              setDemoLoadPercent(true);
            } else {
              console.error(e);
            }
          });
        break;
      case COMPARISON_STATE_PARAM:
        Promise.all([getDemoData(state), getDemoData(comparison_state)])
          .then(([state_data, comp_state_data]) => {
            config.VIEW_DATA = [...state_data, ...comp_state_data];
            setDemoLoadPercent(1);
            calculateData();
            activeDemoRequest.current = [];
          })
          .catch((e) => {
            if (e !== "user cancelled" && !e.name === "AbortError") {
              setDemoErrorMessage("An error occurred while fetching data");
              setDemoLoadPercent(true);
            } else {
              console.error(e);
            }
          });
        break;
      default:
        getDemoData(view_type)
          .then((data) => {
            config.VIEW_DATA = data;
            setDemoLoadPercent(1);
            calculateData();
            activeDemoRequest.current = [];
          })
          .catch((e) => {
            if (e !== "user cancelled" && !e.name === "AbortError") {
              setDemoErrorMessage("An error occurred while fetching data");
              setDemoLoadPercent(true);
            } else {
              console.error(e);
            }
          });
    }
    // Dynamically include countPrefix as a dependency only if vote_mode is true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    geo_type,
    state,
    comparison_state,
    view_type,
    vote_mode,
    count_prefix_dep,
  ]);

  return {
    demoLoadPercent,
    filteredData,
    totalTurnout,
    calculatedData,
    demoErrorMessage,
    lastModified,
    showNoRegPartyWarning,
    demoHeader,
  };
}
